<template>
  <div class="min-h-screen bg-bg pb-[88px] desktop:pb-12">
    <div class="mx-auto max-w-[1320px] px-4 py-6 tablet:px-6 desktop:px-10 desktop:py-10">
      <!-- Breadcrumb -->
      <nav
        class="breadcrumb mono"
        :aria-label="t('public.developers.breadcrumbAria')"
      >
        <NuxtLink to="/developers">{{ t('public.developers.allDevelopers') }}</NuxtLink>
        <span class="sep">/</span>
        <span class="current">{{ developer?.name ?? '…' }}</span>
      </nav>

      <!-- ───────── Skeleton while loading ───────── -->
      <div
        v-if="isLoading && !developer"
        class="hero-grid"
      >
        <div class="skel-hero" />
        <div class="skel-rail" />
      </div>

      <!-- ───────── Not found ───────── -->
      <div
        v-else-if="!developer && !isLoading"
        class="empty"
      >
        <h3 class="font-serif">{{ t('public.developerDetail.notFound.title') }}</h3>
        <p>
          {{ t('public.developerDetail.notFound.description') }}
          <NuxtLink
            to="/developers"
            class="font-semibold"
            >{{ t('public.developerDetail.notFound.back') }}</NuxtLink
          >
        </p>
      </div>

      <template v-else-if="developer">
        <!-- ───────── HERO ───────── -->
        <header class="hero">
          <div class="hero-grid">
            <div>
              <h1 class="title font-serif">
                <span
                  class="dev-mark"
                  :style="{ background: developer.logoUrl ? '#fff' : brandColor }"
                >
                  <img
                    v-if="developer.logoUrl"
                    :src="developer.logoUrl"
                    :alt="developer.name"
                    class="dev-mark-img"
                  />
                  <template v-else>{{ getDeveloperInitial(developer.name) }}</template>
                </span>
                {{ developer.name }}
              </h1>

              <p
                v-if="developer.description"
                class="hero-description"
              >
                {{ developer.description }}
              </p>

              <div
                v-if="visibleTags.length"
                class="hero-tags"
              >
                <span
                  v-for="tag in visibleTags"
                  :key="tag"
                  class="tag"
                  >{{ tag }}</span
                >
                <span
                  v-if="hiddenTagCount > 0"
                  class="tag"
                  >{{ t('public.developerDetail.moreTags', { count: hiddenTagCount }) }}</span
                >
              </div>
            </div>

            <aside class="hero-actions">
              <div class="activity-card">
                <span class="eyebrow mono">{{ t('public.developerDetail.activity90d') }}</span>
                <div class="activity-row1">
                  <i18n-t
                    keypath="public.developerDetail.eventsCount"
                    :plural="developer.activityCount90d"
                    scope="global"
                    tag="span"
                    class="activity-count-label eyebrow mono"
                  >
                    <template #count
                      ><span class="activity-count font-serif tabular">{{ developer.activityCount90d }}</span></template
                    >
                  </i18n-t>
                  <span class="live-dot">
                    <span class="dot" />
                    <span class="mono">{{ t('public.developerDetail.live') }}</span>
                  </span>
                </div>
              </div>
              <button
                type="button"
                class="btn"
                :class="isWatched ? 'btn-secondary' : 'btn-primary'"
                :disabled="toggleWatching.isPending.value"
                @click="onToggleWatch"
              >
                {{ isWatched ? t('public.developerDetail.watching') : t('public.developerDetail.watch') }}
              </button>
              <NuxtLink
                :to="{ path: '/explore', query: { developer_names: developer.name } }"
                class="btn btn-secondary"
                ><span
                  >{{ t('public.developerDetail.exploreProjects') }}
                  <span
                    class="inline-block rtl:-scale-x-100"
                    aria-hidden="true"
                    >→</span
                  ></span
                ></NuxtLink
              >
            </aside>
          </div>

          <!-- Stats strip -->
          <div class="stats-strip">
            <div class="stat">
              <span class="label mono">{{ t('public.developers.card.projects') }}</span>
              <span class="value font-serif tabular">{{ developer.projectCount }}</span>
              <span class="sub">{{ t('public.developerDetail.stats.projectsHint') }}</span>
            </div>
            <div class="stat">
              <span class="label mono">{{ t('public.developers.card.availableUnits') }}</span>
              <span class="value font-serif tabular">{{ formatUnits(developer.unitsCount) }}</span>
              <span class="sub">{{ t('public.developerDetail.stats.unitsHint') }}</span>
            </div>
            <div class="stat">
              <span class="label mono">{{ t('public.developers.card.priceRange') }}</span>
              <span class="value s font-serif tabular">{{
                formatPriceRange(developer.unitPriceMin, developer.unitPriceMax)
              }}</span>
              <span class="sub">{{ t('public.developerDetail.stats.priceHint') }}</span>
            </div>
            <div class="stat">
              <span class="label mono">{{ t('public.developers.sort.launches') }}</span>
              <span class="value font-serif tabular">{{ developer.launches30d }}</span>
              <span
                v-if="latestLaunchName"
                class="badge mono"
                >{{ latestLaunchName }}</span
              >
            </div>
          </div>
        </header>

        <!-- ───────── Status mix + Handover ───────── -->
        <section class="section">
          <div class="charts-row">
            <!-- Status mix -->
            <div class="card">
              <div class="card-head">
                <h2 class="card-title font-serif">{{ t('public.developerDetail.statusMix.title') }}</h2>
                <span class="eyebrow mono">{{
                  t('public.developerDetail.statusMix.total', { count: developer.projectCount })
                }}</span>
              </div>
              <ul class="status-list">
                <li
                  v-for="bucket in statusBuckets"
                  :key="bucket.key"
                  class="status-row"
                >
                  <span class="status-name">
                    <span
                      class="swatch"
                      :style="{ background: bucket.color }"
                    />
                    {{ bucket.label }}
                  </span>
                  <span class="status-bar">
                    <span
                      class="fill"
                      :style="{ width: bucket.pct + '%', background: bucket.color }"
                    />
                  </span>
                  <span class="status-count font-serif tabular">{{ bucket.count }}</span>
                  <span class="status-pct mono">{{ bucket.pct }}%</span>
                </li>
              </ul>
              <div class="status-total">
                <span class="eyebrow mono">{{ t('public.developerDetail.statusMix.totalTracked') }}</span>
                <span class="num font-serif tabular">{{
                  t('public.developerDetail.projectCount', { count: developer.projectCount }, developer.projectCount)
                }}</span>
              </div>
            </div>

            <!-- Handover horizon -->
            <div class="card">
              <div class="card-head">
                <h2 class="card-title font-serif">{{ t('public.developerDetail.handover.title') }}</h2>
                <span class="eyebrow mono">{{
                  t('public.developerDetail.handover.summary', {
                    tracked: developer.projectCount,
                    tba: handover?.tba ?? 0,
                  })
                }}</span>
              </div>
              <!-- Chart plot ONLY: quarter bars + the y-axis scale run chronological
                   (oldest → newest); pin LTR so CSS grid's RTL auto-placement can't
                   reverse the timeline. The heading above and legend/footnote below
                   (translatable "Past"/"Future"/"Peak quarter" labels) stay logical. -->
              <div
                v-if="handover"
                class="handover-wrap"
                dir="ltr"
              >
                <div class="handover-yaxis">
                  <span>{{ handoverMax }}</span>
                  <span>{{ Math.round(handoverMax * 0.75) }}</span>
                  <span>{{ Math.round(handoverMax * 0.5) }}</span>
                  <span>{{ Math.round(handoverMax * 0.25) }}</span>
                  <span>0</span>
                </div>
                <div class="handover-grid">
                  <div
                    v-for="yearBucket in handover.buckets"
                    :key="yearBucket.year"
                    class="handover-year"
                    :data-year="yearBucket.year"
                  >
                    <div
                      v-for="q in [1, 2, 3, 4]"
                      :key="q"
                      :class="['q-bar', barClass(yearBucket, q)]"
                      :style="barStyle(yearBucket, q)"
                      :title="
                        t(
                          'public.developerDetail.handover.barTitle',
                          { year: yearBucket.year, quarter: q, count: barCount(yearBucket, q) },
                          barCount(yearBucket, q),
                        )
                      "
                    >
                      <span
                        v-if="barCount(yearBucket, q) >= 4"
                        class="count mono"
                        >{{ barCount(yearBucket, q) }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div class="handover-footnote mono">
                <span class="legend-item"
                  ><span
                    class="swatch"
                    style="background: #3d3f44"
                  />
                  {{ t('public.developerDetail.handover.past') }}</span
                >
                <span class="legend-item"
                  ><span
                    class="swatch"
                    style="background: #ffb648"
                  />
                  {{ t('public.developerDetail.handover.future') }}</span
                >
                <span class="legend-item"
                  ><span
                    class="swatch"
                    :style="{ background: brandColor }"
                  />
                  {{ t('public.developerDetail.handover.peakQuarter') }}</span
                >
                <i18n-t
                  scope="global"
                  v-if="handover && handover.peakCount > 0"
                  keypath="public.developerDetail.handover.peak"
                  tag="span"
                  class="peak-info mono"
                >
                  <template #detail>
                    <strong>{{
                      t(
                        'public.developerDetail.handover.peakDetail',
                        { count: handover.peakCount, year: handover.peakYear, quarter: handover.peakQuarter },
                        handover.peakCount,
                      )
                    }}</strong>
                  </template>
                </i18n-t>
              </div>
            </div>
          </div>

          <!-- Contact + Recent activity -->
          <div class="activity-row">
            <!-- Contact card. Public/lead view shows a locked teaser instead
                 of commission % + direct contacts; verified agents see the
                 real numbers below. -->
            <div
              v-if="!isAgent"
              class="card locked-card"
            >
              <div
                class="locked-icon"
                aria-hidden="true"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect
                    x="4"
                    y="11"
                    width="16"
                    height="10"
                    rx="2"
                  />
                  <path d="M8 11V8a4 4 0 1 1 8 0v3" />
                </svg>
              </div>
              <h3 class="locked-title font-serif">{{ t('public.developerDetail.locked.commissionTitle') }}</h3>
              <p class="locked-sub">
                {{ t('public.developerDetail.locked.commissionDescription') }}
              </p>
              <div class="locked-actions">
                <button
                  type="button"
                  class="btn btn-gold"
                  @click="onGuestSignup"
                >
                  <span
                    >{{ t('public.developerDetail.locked.signUp') }}
                    <span
                      class="inline-block rtl:-scale-x-100"
                      aria-hidden="true"
                      >→</span
                    ></span
                  >
                </button>
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click="onGuestSignin"
                >
                  {{ t('public.developerDetail.locked.signIn') }}
                </button>
              </div>
            </div>

            <div
              v-else
              class="card"
            >
              <div class="card-head">
                <h2 class="card-title font-serif">{{ t('public.developerDetail.contact.title') }}</h2>
              </div>
              <div class="contact-rows">
                <div
                  class="contact-row"
                  :class="{ top: showLongCommission }"
                >
                  <div class="contact-icon">💰</div>
                  <div class="meta">
                    <div class="label mono">{{ t('public.developerDetail.contact.commission') }}</div>
                    <div
                      v-if="developer.commissionPercent != null"
                      class="value lg font-serif tabular"
                    >
                      {{ developer.commissionPercent
                      }}<span class="unit">{{ t('public.developerDetail.contact.percentStandard') }}</span>
                    </div>
                    <div
                      v-else-if="developer.commissionText"
                      class="commission-text"
                    >
                      <p
                        class="commission-body"
                        :class="{ clamped: !commissionExpanded }"
                      >
                        {{ developer.commissionText }}
                      </p>
                      <button
                        v-if="showLongCommission"
                        type="button"
                        class="commission-toggle"
                        @click="commissionExpanded = !commissionExpanded"
                      >
                        {{
                          commissionExpanded
                            ? t('public.developerDetail.contact.showLess')
                            : t('public.developerDetail.contact.showMore')
                        }}
                      </button>
                    </div>
                    <div
                      v-else
                      class="value lg font-serif tabular"
                    >
                      —
                    </div>
                  </div>
                </div>

                <div class="contact-row">
                  <div class="contact-icon">🕐</div>
                  <div class="meta">
                    <div class="label mono">{{ t('public.developerDetail.contact.lastSync') }}</div>
                    <div class="value">
                      {{
                        t('public.developerDetail.contact.lastSyncValue', {
                          time: formatRelative(developer.lastSyncAt),
                        })
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <DevSalesManagerCard
                v-if="developer.salesManagers.length"
                class="sales-manager-block"
                :developer-name="developer.name"
                :managers="developer.salesManagers"
              />

              <div class="contact-actions">
                <NuxtLink
                  to="/deals/register"
                  class="btn btn-gold"
                  >{{ t('public.developerDetail.contact.registerDeal') }}</NuxtLink
                >
                <NuxtLink
                  to="/profile"
                  class="btn btn-secondary"
                  >{{ t('public.developerDetail.contact.contactAm') }}</NuxtLink
                >
              </div>
            </div>

            <!-- Activity feed. Public/lead view shows a locked teaser — the
                 feed reveals live commercial intel (launches, brochures,
                 inventory changes) and is agent-only. -->
            <div
              v-if="!isAgent"
              class="card locked-card"
            >
              <div
                class="locked-icon"
                aria-hidden="true"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect
                    x="4"
                    y="11"
                    width="16"
                    height="10"
                    rx="2"
                  />
                  <path d="M8 11V8a4 4 0 1 1 8 0v3" />
                </svg>
              </div>
              <h3 class="locked-title font-serif">{{ t('public.developerDetail.locked.activityTitle') }}</h3>
              <p class="locked-sub">
                {{ t('public.developerDetail.locked.activityDescription', { name: developer.name }) }}
              </p>
              <div class="locked-actions">
                <button
                  type="button"
                  class="btn btn-gold"
                  @click="onGuestSignup"
                >
                  <span
                    >{{ t('public.developerDetail.locked.signUp') }}
                    <span
                      class="inline-block rtl:-scale-x-100"
                      aria-hidden="true"
                      >→</span
                    ></span
                  >
                </button>
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click="onGuestSignin"
                >
                  {{ t('public.developerDetail.locked.signIn') }}
                </button>
              </div>
            </div>

            <div
              v-else
              class="card"
            >
              <div class="card-head">
                <h2 class="card-title font-serif">{{ t('public.developerDetail.feed.title') }}</h2>
                <span class="eyebrow mono">{{
                  t('public.developerDetail.feed.latest', {
                    shown: Math.min(10, activity.length),
                    total: activity.length,
                  })
                }}</span>
              </div>
              <ul
                v-if="activity.length"
                class="feed"
              >
                <li
                  v-for="item in activity.slice(0, 10)"
                  :key="item.id"
                  class="feed-item"
                >
                  <span
                    class="evt-pill mono"
                    :data-type="evtType(item)"
                    >{{ evtLabel(item) }}</span
                  >
                  <span class="feed-time mono">{{ formatRelativeShort(item.timestamp) }}</span>
                  <div class="feed-body">
                    <p
                      v-if="item.summary"
                      class="feed-text"
                    >
                      {{ item.summary }}
                    </p>
                    <span
                      v-if="item.projectName"
                      class="feed-project mono"
                    >
                      <span class="dev">{{ developer.name.toUpperCase().slice(0, 8) }}</span>
                      {{ item.projectName }}
                      <span
                        v-if="item.projectIsNew"
                        class="new"
                        >{{ t('public.developerDetail.feed.new') }}</span
                      >
                    </span>
                  </div>
                </li>
              </ul>
              <p
                v-else
                class="feed-empty"
              >
                {{ t('public.developerDetail.feed.empty') }}
              </p>
              <div
                v-if="activity.length > 10"
                class="feed-footer"
              >
                <span class="eyebrow mono">{{
                  t('public.developerDetail.feed.footer', { time: formatRelative(developer.lastSyncAt) })
                }}</span>
              </div>
            </div>
          </div>

          <UpcomingPresentations
            class="mt-12"
            :developer-id="developer.id"
          />

          <!-- ───────── Projects ───────── -->
          <div
            id="projects"
            class="section"
            style="padding-top: 48px"
          >
            <div class="projects-head">
              <div>
                <h2 class="section-title font-serif">{{ t('public.developers.card.projects') }}</h2>
                <p class="section-sub">{{ t('public.developerDetail.projects.subtitle', { name: developer.name }) }}</p>
              </div>
              <div class="projects-meta">
                <i18n-t
                  keypath="public.developerDetail.projects.count"
                  :plural="projectsTotal"
                  scope="global"
                  tag="span"
                  class="projects-count-label eyebrow mono"
                >
                  <template #count
                    ><span class="projects-count font-serif tabular">{{ projectsTotal }}</span></template
                  >
                </i18n-t>
              </div>
            </div>

            <div class="projects-toolbar">
              <div class="search">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="text-black-60"
                >
                  <circle
                    cx="11"
                    cy="11"
                    r="8"
                  />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  v-model="projectSearch"
                  :placeholder="t('public.developerDetail.projects.searchPlaceholder')"
                />
              </div>
              <div class="toolbar-sep" />
              <div class="chips">
                <button
                  v-for="c in projectStatusChips"
                  :key="c.key"
                  type="button"
                  class="chip mono"
                  :class="{ 'is-active': projectStatusFilter === c.key }"
                  @click="onProjectStatusChange(c.key)"
                >
                  {{ c.label }}
                  <span class="count">{{ c.count }}</span>
                </button>
              </div>
              <div class="toolbar-sep" />
              <select
                v-model="projectSort"
                class="sort-btn mono"
              >
                <option value="activity">{{ t('public.developers.sort.activity') }}</option>
                <option value="handover">{{ t('public.developerDetail.projects.sortHandover') }}</option>
                <option value="units">{{ t('public.developerDetail.projects.sortPrice') }}</option>
                <option value="name">{{ t('public.developers.sort.name') }}</option>
              </select>
            </div>

            <div
              v-if="projectsLoading && !projects.length"
              class="projects-grid"
            >
              <div
                v-for="i in 8"
                :key="i"
                class="proj-skeleton"
              />
            </div>

            <div
              v-else-if="!projects.length"
              class="empty"
            >
              <h3 class="font-serif">{{ t('public.developerDetail.projects.emptyTitle') }}</h3>
              <p>{{ t('public.developerDetail.projects.emptyDescription') }}</p>
            </div>

            <div
              v-else
              class="projects-grid"
            >
              <NuxtLink
                v-for="p in projects"
                :key="p.id"
                :to="projectDetailRoute(p)"
                class="proj-card"
              >
                <div class="proj-art">
                  <video
                    v-if="p.coverVideoUrl && p.coverImageUrl == null"
                    :src="p.coverVideoUrl"
                    muted
                    loop
                    playsinline
                    autoplay
                    preload="metadata"
                    class="art-media"
                  />
                  <img
                    v-else-if="p.coverImageUrl && !brokenImageIds.has(p.id)"
                    :src="p.coverImageUrl"
                    :alt="p.name"
                    class="art-media"
                    loading="lazy"
                    @error="brokenImageIds.add(p.id)"
                  />
                  <PropertiesUpcomingPlaceholder v-else />
                  <div class="topleft">
                    <span
                      class="proj-pill"
                      :class="statusPillClass(p.status)"
                      >{{ statusPillLabel(p.status) }}</span
                    >
                    <span
                      v-if="p.isNewLaunch && p.status !== 'launch'"
                      class="proj-pill gold"
                      >{{ t('public.developerDetail.projects.newLaunch') }}</span
                    >
                    <span
                      v-if="p.isDeveloperSubmitted"
                      class="proj-pill gold"
                      >{{ t('devRepProjects.public.newFromDeveloper') }}</span
                    >
                  </div>
                  <div class="bottomleft">
                    <span
                      class="dev-avatar"
                      :style="{ background: brandColor }"
                    >
                      {{ developer.name.toUpperCase().split(' ')[0].slice(0, 8) }}
                    </span>
                  </div>
                </div>
                <div class="proj-body">
                  <h3 class="proj-name font-serif">{{ p.name }}</h3>
                  <p class="proj-sub">{{ projectSub(p) }}</p>

                  <div class="proj-stats">
                    <div class="proj-stat">
                      <div class="label mono">{{ t('public.developerDetail.projects.sortHandover') }}</div>
                      <div class="value tabular">{{ formatDate(p.handover) }}</div>
                    </div>
                    <div
                      v-if="p.serviceChargePerSqft != null"
                      class="proj-stat"
                      style="text-align: end"
                    >
                      <div class="label mono">{{ t('public.developerDetail.projects.serviceCharge') }}</div>
                      <div class="value tabular">AED {{ p.serviceChargePerSqft }}</div>
                    </div>
                  </div>

                  <div
                    v-if="p.progressPercent != null"
                    class="proj-progress"
                  >
                    <span class="bar">
                      <span
                        class="fill"
                        :class="{ done: p.progressPercent === 100 }"
                        :style="{ width: p.progressPercent + '%' }"
                      />
                    </span>
                    <span class="val mono"
                      >{{
                        p.progressPercent.toFixed(p.progressPercent === 100 || p.progressPercent === 0 ? 0 : 1)
                      }}%</span
                    >
                  </div>

                  <span
                    v-if="p.finishes"
                    class="proj-furnish mono"
                    >{{ p.finishes }}</span
                  >
                </div>
              </NuxtLink>
            </div>

            <!-- Pagination -->
            <div
              v-if="projectsTotalPages > 1"
              class="pagination"
            >
              <i18n-t
                scope="global"
                keypath="public.developerDetail.projects.pageCounter"
                :plural="projectsTotal"
                tag="span"
                class="page-counter mono"
              >
                <template #current>
                  <span class="cur">{{ projectsPage }}</span>
                </template>
                <template #total>{{ projectsTotalPages }}</template>
                <template #count>{{ projectsTotal }}</template>
              </i18n-t>
              <div class="page-controls">
                <button
                  class="page-btn mono"
                  :disabled="projectsPage === 1"
                  @click="projectsPage = Math.max(1, projectsPage - 1)"
                >
                  <span
                    ><span
                      class="inline-block rtl:-scale-x-100"
                      aria-hidden="true"
                      >‹</span
                    >
                    {{ t('public.developers.prev') }}</span
                  >
                </button>
                <button
                  v-for="p in projectPageNumbers"
                  :key="p"
                  class="page-btn mono"
                  :class="{ 'is-active': p === projectsPage }"
                  :disabled="p === projectsPage"
                  @click="projectsPage = p"
                >
                  {{ p }}
                </button>
                <button
                  class="page-btn mono"
                  :disabled="projectsPage === projectsTotalPages"
                  @click="projectsPage = Math.min(projectsTotalPages, projectsPage + 1)"
                >
                  <span
                    >{{ t('public.developers.next') }}
                    <span
                      class="inline-block rtl:-scale-x-100"
                      aria-hidden="true"
                      >›</span
                    ></span
                  >
                </button>
              </div>
            </div>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDeveloperDetailQuery, type DeveloperActivityItem } from '~/api/queries/useDeveloperDetailQuery';
import {
  useDeveloperProjectsQuery,
  type DeveloperProjectRow,
  type DeveloperProjectStatus,
} from '~/api/queries/useDeveloperProjectsQuery';
import { useWatchedDevelopersQuery } from '~/api/queries/useWatchedDevelopersQuery';
import { useToggleWatchDeveloper } from '~/api/mutations/useToggleWatchDeveloper';
import { getDeveloperBrandColor, getDeveloperInitial } from '~/data/developer-brand-colors';
import DevSalesManagerCard from '@/components/property/access/DevSalesManagerCard.vue';
import UpcomingPresentations from '@/components/developerPartner/presentations/UpcomingPresentations.vue';

definePageMeta({
  layout: 'default',
});

const { t } = useI18n();
const { formatNumber, formatDate: formatLocaleDate } = useFormat();

const route = useRoute();
const slug = computed(() => String(route.params.slug ?? ''));

// SSR fetch — populates head meta + initial render for crawlers. Uses the
// public endpoint (no auth header) so the SSR'd HTML never contains
// agent-only commercial data. The TanStack Query below takes over on the
// client and refetches with the auth token when present.
const { data: ssrPayload } = await useFetch<{ success: boolean; data: DeveloperDetailPayload }>(
  () => `/api/developers/by-slug/${encodeURIComponent(slug.value)}`,
  {
    key: () => `developer-detail-${slug.value}`,
    server: true,
  },
);

const { data: payload, isLoading } = useDeveloperDetailQuery(slug);
const developer = computed(() => payload.value?.developer ?? ssrPayload.value?.data?.developer ?? null);
const handover = computed(() => payload.value?.handover ?? ssrPayload.value?.data?.handover ?? null);
// Hide generic "Project update" and "Info request" entries from the feed —
// they're noisy and don't carry much signal for agents. Keep LAUNCH /
// BROCHURE / HANDOVER / INVENTORY.
const activity = computed<DeveloperActivityItem[]>(() =>
  (payload.value?.activity ?? ssrPayload.value?.data?.activity ?? []).filter(
    (a) => a.category !== 'UPDATE' && a.category !== 'INTEL',
  ),
);

useDeveloperSeo(developer);

const brandColor = computed(() => getDeveloperBrandColor(slug.value));

// Commission can be a clean "5% standard" OR a long promo paragraph (e.g. a
// tiered-rewards blurb). Render the paragraph as readable body copy, clamped
// with a Show more toggle so it never blows out the card.
const commissionExpanded = ref(false);
const showLongCommission = computed(
  () => developer.value?.commissionPercent == null && (developer.value?.commissionText?.length ?? 0) > 120,
);

// ── Access level — gates commission/contact card + watching ──────────────
const accessLevelComputed = useAccessLevel();
const isAgent = computed(() => accessLevelComputed.value === 'verified');

// Attribute organic signups to the developer page that brought the visitor in.
// Guests only; first-touch wins; read server-side at signup (captureAttribution).
if (import.meta.client) {
  const { recordRegistrationSource } = useTracking();
  watch(
    developer,
    (d) => {
      if (accessLevelComputed.value !== 'guest' || !d?.name) return;
      recordRegistrationSource({ type: 'developer', slug: slug.value, name: String(d.name).trim() });
    },
    { immediate: true },
  );
}
const { buildRedirectUrl } = useRedirectAfterAuth();
const onGuestSignup = () => navigateTo(buildRedirectUrl('/auth/register'));
const onGuestSignin = () => navigateTo(buildRedirectUrl('/auth/login'));

// ── Watching ────────────────────────────────────────────────────────────
const { data: watched } = useWatchedDevelopersQuery();
const watchedIds = computed<number[]>(() => watched.value ?? []);
const isWatched = computed(() => (developer.value ? watchedIds.value.includes(developer.value.id) : false));
const toggleWatching = useToggleWatchDeveloper();
const onToggleWatch = async () => {
  // Watching persists to the user's profile, so it needs an account — but ANY
  // authenticated user can watch (the endpoint is getAuthUser-gated, not agent-
  // only). Only bounce genuine guests to signup; previously this gated on the
  // 'verified' tier (phoneVerified), which wrongly redirected logged-in but
  // not-yet-phone-verified users to the signup page instead of watching.
  if (accessLevelComputed.value === 'guest') {
    onGuestSignup();
    return;
  }
  if (!developer.value) return;
  await toggleWatching.mutateAsync({ developerId: developer.value.id, watching: !isWatched.value });
};

// ── Hero tags ───────────────────────────────────────────────────────────
const visibleTags = computed(() => (developer.value?.allCommunities ?? []).slice(0, 8));
const hiddenTagCount = computed(() => Math.max(0, (developer.value?.allCommunities.length ?? 0) - 8));

// ── Stats helpers ───────────────────────────────────────────────────────
const formatUnits = (count: number) => {
  if (!Number.isFinite(count) || count <= 0) return '—';
  return formatNumber(count);
};
const formatPrice = (value: number | null): string => {
  if (value == null || !Number.isFinite(value) || value <= 0) return '—';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1).replace(/\.0$/, '')}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return String(value);
};
const formatPriceRange = (min: number | null, max: number | null) => {
  if (min == null && max == null) return t('public.developers.card.tba');
  if (min != null && max != null) return `${formatPrice(min)} – ${formatPrice(max)}`;
  return formatPrice(min ?? max);
};

// Most recent launch project name — pull from activity feed (project_launch).
const latestLaunchName = computed(() => {
  const launch = activity.value.find((a) => a.category === 'LAUNCH' && a.projectName);
  return launch?.projectName ?? null;
});

// ── Status mix ──────────────────────────────────────────────────────────
const statusBuckets = computed(() => {
  if (!developer.value) return [];
  const mix = developer.value.statusMix;
  const total = mix.unknown + mix.available + mix.sold + mix.launch || 1;
  const buckets = [
    { key: 'unknown', label: t('public.developers.status.unknown'), count: mix.unknown, color: '#b6b7b9' },
    { key: 'available', label: t('public.developers.status.available'), count: mix.available, color: '#21cb99' },
    { key: 'sold', label: t('public.developers.status.soldOut'), count: mix.sold, color: '#ee4925' },
    { key: 'launch', label: t('public.developers.status.launch'), count: mix.launch, color: '#ffb648' },
  ];
  return buckets.map((b) => ({ ...b, pct: Math.round((b.count / total) * 100) }));
});

// ── Handover histogram ──────────────────────────────────────────────────
const handoverMax = computed(() => {
  if (!handover.value) return 4;
  let max = 0;
  for (const b of handover.value.buckets) {
    max = Math.max(max, b.q1, b.q2, b.q3, b.q4);
  }
  return Math.max(4, Math.ceil(max / 4) * 4);
});
const NOW_YEAR = new Date().getFullYear();
const NOW_Q = Math.floor(new Date().getMonth() / 3) + 1;
const barCount = (yearBucket: { year: number; q1: number; q2: number; q3: number; q4: number }, q: number) => {
  if (q === 1) return yearBucket.q1;
  if (q === 2) return yearBucket.q2;
  if (q === 3) return yearBucket.q3;
  return yearBucket.q4;
};
const barClass = (yearBucket: { year: number }, q: number) => {
  const count = barCount(yearBucket as any, q);
  if (count === 0) return 'empty';
  const past = yearBucket.year < NOW_YEAR || (yearBucket.year === NOW_YEAR && q <= NOW_Q);
  if (handover.value && yearBucket.year === handover.value.peakYear && q === handover.value.peakQuarter) return 'peak';
  return past ? 'past' : 'future';
};
const barStyle = (yearBucket: { q1: number; q2: number; q3: number; q4: number }, q: number) => {
  const count = barCount(yearBucket as any, q);
  if (count === 0) return {};
  const max = handoverMax.value || 1;
  return { height: `${(count / max) * 100}%` };
};

// ── Activity feed pills ─────────────────────────────────────────────────
const evtType = (item: DeveloperActivityItem) => {
  switch (item.category) {
    case 'LAUNCH':
      return 'launch';
    case 'BROCHURE':
      return 'brochure';
    case 'HANDOVER':
      return 'handover';
    case 'INTEL':
      return 'info';
    default:
      return 'update';
  }
};
const evtLabel = (item: DeveloperActivityItem) => {
  switch (item.category) {
    case 'LAUNCH':
      return t('public.developerDetail.feedEvents.launch');
    case 'BROCHURE':
      return t('public.developerDetail.feedEvents.brochure');
    case 'HANDOVER':
      return t('public.developerDetail.feedEvents.handover');
    case 'INTEL':
      return t('public.developerDetail.feedEvents.info');
    case 'INVENTORY':
      return t('public.developerDetail.feedEvents.inventory');
    default:
      return t('public.developerDetail.feedEvents.update');
  }
};

// ── Time formatting ─────────────────────────────────────────────────────
const formatRelative = (iso: string | null) => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return t('public.developerDetail.time.minutesAgo', { count: Math.max(1, minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('public.developerDetail.time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return t('public.developerDetail.time.daysAgo', { count: days });
  return formatLocaleDate(iso, { month: 'short', day: 'numeric' });
};
const formatRelativeShort = (iso: string | null) => formatRelative(iso);
const formatDate = (iso: string | null) => {
  if (!iso) return t('public.developers.card.tba');
  return formatLocaleDate(iso, { month: 'short', year: 'numeric' });
};

// ── Projects grid ───────────────────────────────────────────────────────
const projectSearch = ref('');
const projectStatusFilter = ref<DeveloperProjectStatus | 'all'>('all');
const projectSort = ref<'activity' | 'units' | 'handover' | 'name'>('activity');
const projectsPage = ref(1);

const onProjectStatusChange = (s: DeveloperProjectStatus | 'all') => {
  projectStatusFilter.value = s;
  projectsPage.value = 1;
};
watch([projectSearch, projectSort], () => {
  projectsPage.value = 1;
});

const { data: projectsPayload, isLoading: projectsLoading } = useDeveloperProjectsQuery({
  slug,
  page: projectsPage,
  status: projectStatusFilter,
  search: projectSearch,
  sort: projectSort,
});

const projects = computed<DeveloperProjectRow[]>(() => projectsPayload.value?.rows ?? []);
const projectsTotal = computed(() => projectsPayload.value?.total ?? 0);
const projectsTotalPages = computed(() => projectsPayload.value?.totalPages ?? 1);
const projectPageNumbers = computed<number[]>(() => {
  const last = projectsTotalPages.value;
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
  const c = projectsPage.value;
  if (c <= 4) return [1, 2, 3, 4, 5, last];
  if (c >= last - 3) return [1, last - 4, last - 3, last - 2, last - 1, last];
  return [1, c - 1, c, c + 1, last];
});

// Status chip counts come from the developer's overall status mix.
const projectStatusChips = computed(() => {
  if (!developer.value) return [];
  const mix = developer.value.statusMix;
  return [
    { key: 'all' as const, label: t('public.developers.filters.all'), count: developer.value.projectCount },
    { key: 'launch' as const, label: t('public.developers.status.launch'), count: mix.launch },
    { key: 'available' as const, label: t('public.developers.status.available'), count: mix.available },
    { key: 'sold' as const, label: t('public.developers.status.soldOut'), count: mix.sold },
    { key: 'unknown' as const, label: t('public.developers.status.unknown'), count: mix.unknown },
  ];
});

const projectSub = (p: DeveloperProjectRow) => {
  const parts: string[] = [];
  if (p.district) parts.push(p.district);
  if (p.unitsCount > 0) {
    parts.push(t('public.developerDetail.projects.units', { count: formatNumber(p.unitsCount) }, p.unitsCount));
  }
  return parts.join(' · ') || '—';
};

const statusPillClass = (status: DeveloperProjectStatus) => {
  if (status === 'launch') return 'launch';
  if (status === 'sold') return 'sold';
  return '';
};
const statusPillLabel = (status: DeveloperProjectStatus) => {
  if (status === 'launch') return t('public.developers.status.launch');
  if (status === 'sold') return t('public.developers.status.soldOut');
  if (status === 'available') return t('public.developers.status.available');
  return t('public.developers.status.unknown');
};

// Tracks images that 404 at runtime so we can swap them out for the
// PropertiesUpcomingPlaceholder. Mirrors how ProjectCard on /explore handles
// broken sources — render the "coming soon" placeholder instead of a busted
// <img>.
const brokenImageIds = reactive(new Set<number | string>());

// Prefer the canonical slug URL: /projects/<developerSlug>/<projectSlug>.
// We already know the developer slug (this page's :slug param) and the
// project slug comes back from the API row. Falls back to /properties/<id>
// only when the project hasn't been backfilled into ProjectSlug yet — the
// existing property-id-to-slug-redirect middleware will 301 those to the
// canonical URL once available, but linking to the canonical form
// directly is one fewer hop.
const projectDetailRoute = (p: DeveloperProjectRow): string => {
  if (p.href) return p.href;
  if (p.slug && slug.value) {
    return `/projects/${slug.value}/${p.slug}`;
  }
  const complexId = p.geniemapId != null ? String(p.geniemapId) : `p-${p.id}`;
  return `/properties/${complexId}`;
};
</script>

<style scoped lang="scss">
.mono {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}
.tabular {
  font-variant-numeric: tabular-nums;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #6e6f73;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.breadcrumb a:hover {
  color: #0d0f15;
}
.breadcrumb .sep {
  color: #cfcfd0;
}
.breadcrumb .current {
  color: #0d0f15;
}

.skel-hero,
.skel-rail {
  background: linear-gradient(90deg, #f3f3f3 0%, #e7e7e8 50%, #f3f3f3 100%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: 18px;
}
.skel-hero {
  height: 280px;
}
.skel-rail {
  height: 280px;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* HERO */
.hero {
  padding: 22px 0 24px;
  border-bottom: 1px solid rgba(13, 15, 21, 0.08);
}
.hero-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  align-items: flex-end;
}
@media (min-width: 1024px) {
  .hero-grid {
    grid-template-columns: 1fr auto;
    gap: 32px;
  }
}
.title {
  font-weight: 700;
  font-size: 36px;
  line-height: 1.04;
  letter-spacing: -0.025em;
  margin: 12px 0 0;
  color: #0d0f15;
  display: flex;
  align-items: center;
  gap: 22px;
  flex-wrap: wrap;
}
@media (min-width: 1024px) {
  .title {
    font-size: 48px;
  }
}
.dev-mark {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  color: #fff;
  display: grid;
  place-items: center;
  overflow: hidden;
  font-family: 'Fraunces', Georgia, serif;
  font-weight: 800;
  font-size: 26px;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.6) inset;
}
.dev-mark-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 8px;
  border-radius: 14px;
  background: #fff;
}
.hero-description {
  max-width: 720px;
  margin: 16px 0 0;
  font-size: 14.5px;
  line-height: 1.6;
  color: #3d3f44;
}
.hero-tags {
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(13, 15, 21, 0.08);
  background: #fff;
  font-size: 12px;
  color: #3d3f44;
}
.tag::before {
  content: '·';
  margin-inline-end: 6px;
  color: #9e9fa1;
}

.hero-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  min-width: 220px;
}
.activity-card {
  background: #fff;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.activity-row1 {
  display: flex;
  align-items: center;
  gap: 8px;
}
/* Count + plural noun come from one message; the flex gap keeps the original spacing between them. */
.activity-count-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #3d3f44;
}
.activity-count-label .activity-count {
  color: #0d0f15;
}
.activity-count {
  font-weight: 700;
  font-size: 28px;
  line-height: 1;
  letter-spacing: -0.02em;
}
.live-dot {
  margin-inline-start: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  color: #6e6f73;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
}
.live-dot .dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #21cb99;
  display: inline-block;
}

.btn {
  height: 42px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  padding-inline: 16px;
  border: 0;
}
.btn-primary {
  background: #0d0f15;
  color: #fff;
}
.btn-primary:hover {
  background: #25272c;
}
.btn-secondary {
  background: #fff;
  color: #0d0f15;
  border: 1px solid rgba(13, 15, 21, 0.08);
}
.btn-secondary:hover {
  background: #f3f3f3;
}
.btn-gold {
  background: #ffb648;
  color: #0d0f15;
}
.btn-gold:hover {
  background: #eda436;
}

.locked-card {
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  background: radial-gradient(circle at 100% 0%, rgba(255, 182, 72, 0.16), transparent 55%), #fff;
  border: 1px solid rgba(255, 182, 72, 0.35);
}
.locked-card .locked-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgba(255, 182, 72, 0.18);
  color: #8b6000;
}
.locked-card .locked-title {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #0d0f15;
  margin: 0;
}
.locked-card .locked-sub {
  margin: 0;
  font-size: 13.5px;
  color: #4b4d52;
  line-height: 1.5;
  max-width: 360px;
}
.locked-card .locked-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}

.stats-strip {
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  background: #fff;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 18px;
  overflow: hidden;
}
@media (min-width: 1024px) {
  .stats-strip {
    grid-template-columns: repeat(4, 1fr);
  }
}
.stat {
  padding: 18px 22px;
  border-inline-end: 1px solid rgba(13, 15, 21, 0.08);
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.stat:last-child {
  border-inline-end: 0;
}
.stat .label {
  font-size: 10px;
  font-weight: 600;
  color: #6e6f73;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}
.stat .value {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
}
.stat .value.s {
  font-size: 20px;
}
.stat .sub {
  font-size: 12px;
  color: #6e6f73;
}
.stat .badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff8ed;
  color: #7a5212;
  font-size: 10.5px;
  font-weight: 600;
  margin-top: 2px;
  width: fit-content;
}

/* SECTIONS / CARDS */
.section {
  padding-top: 36px;
}
.section-title {
  font-weight: 700;
  font-size: 22px;
  letter-spacing: -0.015em;
  margin: 0;
}
.section-sub {
  font-size: 13px;
  color: #6e6f73;
  margin-top: 4px;
}
.card {
  background: #fff;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 18px;
  padding: 22px 24px;
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.card-title {
  font-weight: 700;
  font-size: 18px;
  letter-spacing: -0.015em;
  margin: 0;
}

.charts-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}
@media (min-width: 1024px) {
  .charts-row {
    grid-template-columns: 5fr 7fr;
  }
}
.activity-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 24px;
}
@media (min-width: 1024px) {
  .activity-row {
    grid-template-columns: 4fr 8fr;
  }
}

/* Status mix */
.status-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.status-row {
  display: grid;
  grid-template-columns: 110px 1fr 60px 50px;
  align-items: center;
  gap: 14px;
}
.status-name {
  font-size: 13.5px;
  font-weight: 600;
}
.status-name .swatch {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 2px;
  margin-inline-end: 8px;
  transform: translateY(-1px);
}
.status-bar {
  display: block;
  height: 10px;
  background: #f3f3f3;
  border-radius: 5px;
  overflow: hidden;
}
.status-bar .fill {
  display: block;
  height: 100%;
  border-radius: 5px;
  min-width: 2px;
}
.status-count {
  font-weight: 700;
  font-size: 18px;
  text-align: end;
}
.status-pct {
  font-size: 11px;
  color: #6e6f73;
  text-align: end;
}
.status-total {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid rgba(13, 15, 21, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.status-total .num {
  font-weight: 700;
  font-size: 24px;
  letter-spacing: -0.015em;
}

/* Handover */
.handover-wrap {
  position: relative;
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 26px;
}
.handover-grid {
  position: relative;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(34px, 1fr);
  gap: 4px;
  align-items: end;
  height: 180px;
  min-width: 100%;
  padding-inline-start: 28px;
  border-bottom: 1px solid rgba(13, 15, 21, 0.05);
}
.handover-year {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  align-items: end;
  height: 100%;
  position: relative;
}
.handover-year::after {
  content: attr(data-year);
  position: absolute;
  bottom: -22px;
  inset-inline-start: 0;
  inset-inline-end: 0;
  text-align: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: #6e6f73;
  letter-spacing: 0.04em;
}
.q-bar {
  background: #3d3f44;
  border-radius: 2px 2px 0 0;
  position: relative;
  min-height: 1px;
}
.q-bar.empty {
  background: #f3f3f3;
  height: 4px !important;
  border-radius: 2px;
}
.q-bar.future {
  background: #ffb648;
}
.q-bar.peak {
  background: #eda436;
}
.q-bar .count {
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 9.5px;
  color: #0d0f15;
  font-weight: 600;
}
.handover-yaxis {
  position: absolute;
  inset-inline-start: 0;
  top: 0;
  bottom: 0;
  width: 28px;
  height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-family: 'JetBrains Mono', monospace;
  font-size: 9.5px;
  color: #9e9fa1;
}
.handover-footnote {
  margin-top: 38px;
  font-size: 12px;
  color: #6e6f73;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
}
.handover-footnote .legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.handover-footnote .swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}
.handover-footnote .peak-info {
  margin-inline-start: auto;
}

/* Contact rows */
.contact-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.contact-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 0;
  border-top: 1px solid rgba(13, 15, 21, 0.05);
}
.contact-row:first-child {
  border-top: 0;
  padding-top: 0;
}
.contact-row.top {
  align-items: flex-start;
}
.contact-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f3f3f3;
  display: grid;
  place-items: center;
  font-size: 16px;
}
.contact-row .meta {
  flex: 1;
  min-width: 0;
}
.contact-row .label {
  font-size: 11px;
  color: #6e6f73;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
}
.contact-row .value {
  font-size: 14px;
  font-weight: 600;
  color: #0d0f15;
  margin-top: 2px;
}
.contact-row .value.lg {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.015em;
}
.contact-row .value.lg .unit {
  font-weight: 500;
  font-size: 13px;
  color: #6e6f73;
  margin-inline-start: 4px;
}
.commission-text {
  margin-top: 4px;
}
.commission-body {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  font-weight: 500;
  color: #45464a;
  white-space: pre-line;
}
.commission-body.clamped {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.commission-toggle {
  margin-top: 6px;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #0d0f15;
  &:hover {
    text-decoration: underline;
  }
}
.sales-manager-block {
  margin-top: 16px;
}
.contact-actions {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(13, 15, 21, 0.05);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

/* Activity feed */
.feed {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}
.feed-item {
  display: grid;
  grid-template-columns: 130px 60px 1fr;
  gap: 18px;
  align-items: flex-start;
  padding: 14px 0;
  border-top: 1px solid rgba(13, 15, 21, 0.05);
}
.feed-item:first-child {
  border-top: 0;
}
.evt-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: 5px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  width: fit-content;
}
.evt-pill[data-type='update'] {
  background: #e5eeff;
  color: #1f3a9c;
}
.evt-pill[data-type='handover'] {
  background: #dcf5eb;
  color: #126b4b;
}
.evt-pill[data-type='brochure'] {
  background: #fff8ed;
  color: #7a5212;
}
.evt-pill[data-type='info'] {
  background: #f3f3f3;
  color: #3d3f44;
}
.evt-pill[data-type='launch'] {
  background: #fce4dd;
  color: #a03218;
}

.feed-time {
  font-size: 11px;
  color: #6e6f73;
  padding-top: 4px;
}
.feed-text {
  font-size: 13.5px;
  color: #3d3f44;
  line-height: 1.5;
  margin: 0;
}
.feed-project {
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  font-weight: 600;
  color: #6e6f73;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.feed-project .dev {
  color: #3d3f44;
}
.feed-project .new {
  background: #ffb648;
  color: #0d0f15;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 9px;
  letter-spacing: 0.08em;
}
.feed-empty {
  color: #6e6f73;
  font-size: 13.5px;
  padding: 14px 0;
}
.feed-footer {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(13, 15, 21, 0.05);
}

/* Projects */
.projects-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 8px;
}
.projects-meta {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.projects-count-label {
  display: inline-flex;
  align-items: baseline;
  gap: 12px;
}
.projects-count-label .projects-count {
  color: #0d0f15;
}
.projects-count {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.015em;
}
.projects-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 14px;
  padding: 8px;
  margin: 14px 0 22px;
}
.search {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
}
.search input {
  flex: 1;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 13.5px;
  color: #0d0f15;
}
.toolbar-sep {
  width: 1px;
  height: 24px;
  background: rgba(13, 15, 21, 0.08);
}
.chips {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.chip {
  padding: 6px 11px;
  border-radius: 8px;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6e6f73;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.chip.is-active {
  background: #0d0f15;
  color: #fff;
}
.chip .count {
  font-weight: 600;
  opacity: 0.55;
  margin-inline-start: 4px;
}
.chip.is-active .count {
  opacity: 0.65;
}
.sort-btn {
  padding: 6px 12px;
  border-radius: 8px;
  background: #f8f7f3;
  font-size: 12.5px;
  font-weight: 600;
  border: 0;
  outline: 0;
  cursor: pointer;
}

.projects-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}
@media (min-width: 744px) {
  .projects-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (min-width: 1024px) {
  .projects-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
.proj-card {
  background: #fff;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease;
  &:hover {
    box-shadow: 0 4px 18px rgba(13, 15, 21, 0.08);
    transform: translateY(-1px);
  }
}
.proj-skeleton {
  height: 320px;
  border-radius: 16px;
  background: linear-gradient(90deg, #f3f3f3 0%, #e7e7e8 50%, #f3f3f3 100%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
.proj-art {
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: #f3f3f3;
}
.proj-art.empty {
  display: grid;
  place-items: center;
}
.art-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.proj-art .topleft,
.proj-art .bottomleft {
  position: absolute;
  display: flex;
  gap: 6px;
}
.proj-art .topleft {
  top: 12px;
  inset-inline-start: 12px;
}
.proj-art .bottomleft {
  bottom: 12px;
  inset-inline-start: 12px;
}
.proj-pill {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  color: #0d0f15;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.proj-pill.gold {
  background: #ffb648;
}
.proj-pill.sold {
  background: rgba(238, 73, 37, 0.95);
  color: #fff;
}
.proj-pill.launch {
  background: #0d0f15;
  color: #ffb648;
}
.dev-avatar {
  color: #fff;
  padding: 4px 10px;
  border-radius: 8px;
  font-family: 'Fraunces', Georgia, serif;
  font-weight: 800;
  font-size: 11px;
  letter-spacing: 0.04em;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}
.proj-body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}
.proj-name {
  font-weight: 700;
  font-size: 17px;
  line-height: 1.2;
  letter-spacing: -0.015em;
  color: #0d0f15;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.proj-sub {
  font-size: 12.5px;
  color: #6e6f73;
  margin: 0;
}
.proj-stats {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  padding-top: 10px;
  border-top: 1px solid rgba(13, 15, 21, 0.05);
}
.proj-stat .label {
  font-size: 9.5px;
  color: #6e6f73;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
}
.proj-stat .value {
  font-size: 13.5px;
  font-weight: 600;
  margin-top: 2px;
}

.proj-progress {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.proj-progress .bar {
  flex: 1;
  height: 4px;
  background: #f3f3f3;
  border-radius: 999px;
  overflow: hidden;
}
/* .fill is a <span>; without display:block an inline element ignores
   width/height/background, so only the gray track showed. */
.proj-progress .bar .fill {
  display: block;
  height: 100%;
  background: #ffb648;
}
.proj-progress .bar .fill.done {
  background: #43cf58;
}
.proj-progress .val {
  font-size: 10.5px;
  color: #6e6f73;
  font-weight: 600;
  min-width: 38px;
  text-align: end;
}
.proj-furnish {
  margin-top: 12px;
  font-size: 9.5px;
  color: #6e6f73;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.proj-furnish::before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: 999px;
  background: #b6b7b9;
}

.pagination {
  margin-top: 28px;
  padding-top: 22px;
  border-top: 1px solid rgba(13, 15, 21, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.page-counter {
  font-size: 11.5px;
  color: #6e6f73;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
}
.page-counter .cur {
  color: #0d0f15;
}
.page-controls {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.page-btn {
  height: 36px;
  min-width: 36px;
  padding-inline: 12px;
  border-radius: 8px;
  border: 1px solid rgba(13, 15, 21, 0.08);
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.page-btn:hover {
  background: #f3f3f3;
}
.page-btn.is-active {
  background: #0d0f15;
  color: #fff;
  border-color: #0d0f15;
}
.page-btn[disabled] {
  opacity: 0.4;
  cursor: default;
}

.empty {
  text-align: center;
  padding: 80px 24px;
  color: #6e6f73;
  font-size: 14px;
  background: #fff;
  border: 1px dashed rgba(13, 15, 21, 0.08);
  border-radius: 18px;
}
.empty h3 {
  font-weight: 700;
  font-size: 22px;
  color: #0d0f15;
  margin: 0 0 8px;
  letter-spacing: -0.015em;
}

.eyebrow {
  font-size: 10.5px;
  font-weight: 600;
  color: #6e6f73;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}
</style>
